import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Custom hook for managing Mulligan state and actions
 * @param {WebSocket|null} ws - React state socket (may be briefly null during reconnect)
 * @param {string} gameId
 * @param {number|string} userId
 * @param {() => WebSocket|null} [getActiveWebSocket] - Prefer open socket from ref + state (BattleUI); avoids submit with stale null ws
 */
export const useMulligan = (ws, gameId, userId, getActiveWebSocket) => {
  const [mulliganOrders, setMulliganOrders] = useState([]);
  const [needsMulligan, setNeedsMulligan] = useState(false);
  const [mulliganTimeLeft, setMulliganTimeLeft] = useState(null);
  const mulliganStartTimeRef = useRef(null);
  const hasAutoSubmittedRef = useRef(false);
  const currentGameStateRef = useRef(null); // Store latest game state for timer validation
  const submissionInFlightRef = useRef(false); // Prevent concurrent submissions

  /**
   * Update Mulligan orders from game state
   */
  const updateMulliganOrders = useCallback((gameState) => {
    // Store latest game state for timer validation - CRITICAL: Always update ref immediately
    currentGameStateRef.current = gameState;
    
    
    if (!gameState || !gameState.state || !gameState.state[userId]) {
      setMulliganOrders([]);
      setNeedsMulligan(false);
      mulliganStartTimeRef.current = null;
      hasAutoSubmittedRef.current = false;
      submissionInFlightRef.current = false; // Clear submission flag when state is invalid
      return;
    }
    
    // Clear submission flag when we receive a state update (means backend processed our submission)
    const playerState = gameState.state[userId];
    if (playerState.hasMulliganed && !playerState.needsMulligan && !playerState.needsTeamLeaderMulligan) {
      submissionInFlightRef.current = false;
    }
    
    // Check for initial mulligan or team leader meeting mulligan
    const shouldMulligan = (playerState.needsMulligan === true && playerState.hasMulliganed === false) || 
                           (playerState.needsTeamLeaderMulligan === true);
    
    // Track when mulligan starts
    if (shouldMulligan && !mulliganStartTimeRef.current) {
      mulliganStartTimeRef.current = Date.now();
      hasAutoSubmittedRef.current = false;
    } else if (!shouldMulligan) {
      mulliganStartTimeRef.current = null;
      hasAutoSubmittedRef.current = false;
      setMulliganTimeLeft(null);
    }
    
    setNeedsMulligan(shouldMulligan);
    
    // Determine which orders to show for mulligan
    if (shouldMulligan) {
      if (playerState.needsTeamLeaderMulligan === true && playerState.teamLeaderMulliganOrders) {
        // Team leader meeting mulligan - use the selected random orders
        const teamLeaderOrders = playerState.hand?.filter(order => 
          order && playerState.teamLeaderMulliganOrders.includes(order.instanceId)
        ) || [];
        setMulliganOrders([...teamLeaderOrders]);
      } else if (gameState.turnNumber === 1) {
        // Initial mulligan - show first 3 orders from hand
        const initialHand = playerState.hand?.slice(0, 3) || [];
        setMulliganOrders([...initialHand]);
      } else {
        setMulliganOrders([]);
      }
    } else {
      setMulliganOrders([]);
    }
  }, [userId]);

  /**
   * Submit mulligan decision
   */
  const submitMulligan = useCallback(async (selectedOrderInstanceIds) => {
    const activeWs =
      typeof getActiveWebSocket === 'function' ? getActiveWebSocket() : ws;
    const wsOpen = typeof WebSocket !== 'undefined' ? WebSocket.OPEN : 1;
    if (!activeWs || activeWs.readyState !== wsOpen || !gameId) {
      console.error('🔄 submitMulligan: Missing required parameters', {
        hasSocket: !!activeWs,
        readyState: activeWs?.readyState,
        gameId,
      });
      return;
    }

    // ✅ CRITICAL: Prevent concurrent submissions
    if (submissionInFlightRef.current) {
      return;
    }

    // ✅ CRITICAL: Always check backend state first (from currentGameStateRef) - it's the source of truth
    // React state might be stale, so we must verify with backend state
    const gameState = currentGameStateRef.current;
    
    // STRICT VALIDATION: Must have backend state to proceed
    if (!gameState || !gameState.state || !gameState.state[userId]) {
      // Can't verify backend state - BLOCK to be safe (don't trust React state alone)
      return;
    }
    
    // We have backend state - use it as the authority
    const playerState = gameState.state[userId];
    const backendNeedsMulligan = playerState.needsMulligan === true;
    const hasAlreadyMulliganed = playerState.hasMulliganed === true;
    const needsTeamLeaderMulligan = playerState.needsTeamLeaderMulligan === true;
    
    // Check if backend allows mulligan
    const backendAllowsMulligan = (backendNeedsMulligan && !hasAlreadyMulliganed) || needsTeamLeaderMulligan;
    
    if (!backendAllowsMulligan) {
      // Backend says no mulligan needed - block submission
      hasAutoSubmittedRef.current = true;
      mulliganStartTimeRef.current = null;
      setNeedsMulligan(false); // Update React state to match backend
      return;
    }

    // ✅ FINAL CHECK: Re-verify backend state right before sending (defense against race conditions)
    const finalGameState = currentGameStateRef.current;
    if (!finalGameState || !finalGameState.state || !finalGameState.state[userId]) {
      return;
    }
    
    const finalPlayerState = finalGameState.state[userId];
    const finalBackendAllowsMulligan = (finalPlayerState.needsMulligan === true && !finalPlayerState.hasMulliganed) || 
                                       (finalPlayerState.needsTeamLeaderMulligan === true);
    
    if (!finalBackendAllowsMulligan) {
      hasAutoSubmittedRef.current = true;
      mulliganStartTimeRef.current = null;
      setNeedsMulligan(false);
      return;
    }

    // ✅ Backend state allows mulligan - submit
    submissionInFlightRef.current = true; // Mark submission as in flight
    try {
      activeWs.send(JSON.stringify({
        type: "play-turn",
        gameId,
        action: {
          type: "mulligan",
          selectedOrderInstanceIds: selectedOrderInstanceIds
        }
      }));
      // ✅ CRITICAL: Mark as submitted and clear timer to prevent duplicate submissions
      // This prevents the timer from firing after manual submission
      hasAutoSubmittedRef.current = true;
      mulliganStartTimeRef.current = null;
      
      // Clear submission flag after a short delay (allows backend to process)
      // The flag will be cleared when we receive the next game state update
      setTimeout(() => {
        submissionInFlightRef.current = false;
      }, 1000);
    } catch (error) {
      console.error('🔄 Error submitting mulligan:', error);
      submissionInFlightRef.current = false; // Clear flag on error
    }
  }, [ws, gameId, userId, needsMulligan, getActiveWebSocket]);

  // Timer countdown and auto-submit logic
  useEffect(() => {
    if (!needsMulligan || !mulliganStartTimeRef.current) {
      setMulliganTimeLeft(null);
      return;
    }

    // Get mulligan duration from game state (configured in timing.json)
    // Default to 20 seconds if not provided (backward compatibility)
    // If 0, means infinite time (no auto-submit)
    const gameState = currentGameStateRef.current;
    // Explicitly check for undefined/null, but allow 0 (which means infinite time)
    const mulliganDuration = (gameState?.mulliganDurationMs !== undefined && gameState?.mulliganDurationMs !== null) 
      ? gameState.mulliganDurationMs 
      : 20000; // Default 20 seconds only if undefined/null
    const warningTime = 5000; // Show countdown for last 5 seconds
    
    // If duration is 0, disable timer (infinite time)
    if (mulliganDuration === 0) {
      setMulliganTimeLeft(null);
      return;
    }
    
    const updateTimer = () => {
      const now = Date.now();
      const elapsed = now - mulliganStartTimeRef.current;
      const remaining = mulliganDuration - elapsed;
      
      if (remaining <= 0) {
        // Timer expired - auto-submit "keep all" (empty array)
        // ✅ CRITICAL: Check actual game state, not just React state (which might be stale)
        const gameState = currentGameStateRef.current;
        let shouldSubmit = false;
        
        if (gameState && gameState.state && gameState.state[userId]) {
          const playerState = gameState.state[userId];
          const stillNeedsMulligan = (playerState.needsMulligan === true && playerState.hasMulliganed === false) || 
                                    (playerState.needsTeamLeaderMulligan === true);
          shouldSubmit = stillNeedsMulligan && !hasAutoSubmittedRef.current && mulliganStartTimeRef.current;
        }
        
        if (shouldSubmit) {
          hasAutoSubmittedRef.current = true;
          submitMulligan([]);
        }
        setMulliganTimeLeft(null);
        return;
      }
      
      // Only show countdown when 5 seconds or less remain
      if (remaining <= warningTime) {
        setMulliganTimeLeft(Math.ceil(remaining / 1000));
      } else {
        setMulliganTimeLeft(null);
      }
    };

    // Update immediately
    updateTimer();
    
    // Update every second
    const interval = setInterval(updateTimer, 1000);
    
    return () => clearInterval(interval);
  }, [needsMulligan, submitMulligan]);

  return {
    mulliganOrders,
    needsMulligan,
    mulliganTimeLeft,
    updateMulliganOrders,
    submitMulligan
  };
};

export default useMulligan;

